import { Spacing } from '@persona-id/design-tokens';
import { SearchIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useMemo } from 'react';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { TriggerType } from 'dashboard/models/trigger';
import { WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowsFilterParams } from 'dashboard/redux/actions/workflows';
import Card from 'design-system/components/Card';
import Input, { InputGroup } from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { EventName } from 'lib/constants/events';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';

const EVENT_NAME_FILTER_OPTIONS = Object.values(EventName).map((eventName) => ({
  label: eventName,
  value: eventName,
}));

const TRIGGER_FILTER_OPTIONS = [
  {
    label: 'API',
    value: ApiModel.TriggerApi,
  },
  {
    label: 'Event',
    value: ApiModel.TriggerEvent,
  },
  {
    label: 'Schedule',
    value: ApiModel.TriggerSchedule,
  },
  {
    label: 'Recurring',
    value: ApiModel.TriggerRecurring,
  },
];

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: WorkflowStatus.Running,
  },
  {
    label: 'Inactive',
    value: WorkflowStatus.Paused,
  },
];

const SORT_OPTIONS = [
  {
    label: 'Name A to Z',
    value: 'name',
  },
  {
    label: 'Name Z to A',
    value: '-name',
  },
  {
    label: 'Created new to old',
    value: '-created_at',
  },
  {
    label: 'Created old to new',
    value: 'created_at',
  },
  {
    label: 'Edited new to old',
    value: '-updated_at',
  },
  {
    label: 'Edited old to new',
    value: 'updated_at',
  },
];

interface WorkflowsViewConfigurationBarProps {
  workflowsFilterParams: WorkflowsFilterParams;
  workflowsSortParams: SortParams;
  handleOmniFilterUpdate: (omni: string) => void;
  handleSortUpdate: (newSortParams: SortParams) => void;
  handleStatusFilterUpdate: (status: WorkflowStatus[]) => void;
  handleTriggerFilterUpdate: (workflowsTriggers: TriggerType[]) => void;
  handleEventNameFilterUpdate: (eventNames: EventName[]) => void;
}

const FiltersRow = styled('div')<{ showEventName: boolean }>`
  display: grid;
  column-gap: ${Spacing[4]};
  grid-template-columns: ${({ showEventName }) =>
    showEventName ? '1fr 1fr 1fr' : '1.5fr 1fr 1fr 1fr'};
  align-items: end;
  row-gap: ${Spacing[4]};

  &.with-extra-row {
    grid-template-rows: auto auto;
    grid-auto-flow: dense;
  }
`;

const WorkflowsViewConfigurationBar = ({
  workflowsFilterParams,
  workflowsSortParams: { sort, shouldSortDescending },
  handleOmniFilterUpdate,
  handleSortUpdate,
  handleStatusFilterUpdate,
  handleTriggerFilterUpdate,
  handleEventNameFilterUpdate,
}: WorkflowsViewConfigurationBarProps) => {
  const triggerFilterLabelTextMapper = useLabelTextMapper(TRIGGER_FILTER_OPTIONS, 'Triggers', {
    emptyIsAll: true,
  });
  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
    emptyIsAll: true,
  });
  const eventNameFilterLabelTextMapper = useLabelTextMapper(
    EVENT_NAME_FILTER_OPTIONS,
    'Event Names',
    {
      emptyIsAll: true,
    },
  );

  const workflowsSortParamsByString = useMemo(
    () => `${shouldSortDescending ? '-' : ''}${sort}`,
    [sort, shouldSortDescending],
  );

  const updateSortParms = (input: string) => {
    handleSortUpdate({ sort: input, shouldSortDescending: false });
  };

  const [handleOmniFilterChange] = useDebounced(
    (e: ChangeEvent<HTMLInputElement>) => handleOmniFilterUpdate(e.target.value),
    350,
  );

  const isEventSelected = (workflowsFilterParams.triggerTypes || []).includes(
    ApiModel.TriggerEvent,
  );

  const esFilterEnabled = useFeatureFlag('workflows:use-elastic-search-filter');
  const showEventName = isEventSelected && esFilterEnabled;

  return (
    <Card>
      <Card.Body>
        <FiltersRow
          showEventName={showEventName}
          className={isEventSelected ? 'with-extra-row' : ''}
        >
          <div>
            <FormControlLabel htmlFor='workflows-view-configuration-bar__filter-omni'>
              Search
            </FormControlLabel>
            <InputGroup leftIcon={<SearchIcon />}>
              <Input
                id='workflows-view-configuration-bar__filter-omni'
                placeholder='Workflow name'
                size='md'
                defaultValue={workflowsFilterParams.omni}
                onChange={handleOmniFilterChange}
              />
            </InputGroup>
          </div>
          <div>
            <FormControlLabel htmlFor='workflows-view-configuration-bar__select-trigger'>
              Filter By
            </FormControlLabel>
            <MultiSelect
              data-test='workflows-view-configuration-bar__select--trigger'
              labelTextMapper={triggerFilterLabelTextMapper}
              selectOptionValues={TRIGGER_FILTER_OPTIONS}
              size='md'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={workflowsFilterParams.triggerTypes}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              onChange={handleTriggerFilterUpdate}
            />
          </div>
          {showEventName && (
            <div>
              <FormControlLabel htmlFor='workflows-view-configuration-bar__select--event-name'>
                Event Name
              </FormControlLabel>
              <MultiSelect
                data-test='workflows-view-configuration-bar__select--event-name'
                labelTextMapper={eventNameFilterLabelTextMapper}
                selectOptionValues={EVENT_NAME_FILTER_OPTIONS}
                size='md'
                value={workflowsFilterParams.eventNames || []}
                onChange={handleEventNameFilterUpdate}
                searchEnabled={true}
              />
            </div>
          )}
          <div>
            <FormControlLabel htmlFor='workflows-view-configuration-bar__select--status'>
              Status
            </FormControlLabel>
            <MultiSelect
              data-test='workflows-view-configuration-bar__select--status'
              labelTextMapper={statusFilterLabelTextMapper}
              selectOptionValues={STATUS_FILTER_OPTIONS}
              size='md'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={workflowsFilterParams.status}
              onChange={handleStatusFilterUpdate}
            />
          </div>
          <div>
            <FormControlLabel htmlFor='workflows-view-configuration-bar__select--sort'>
              Sort By
            </FormControlLabel>
            <Select
              id='workflows-view-configuration-bar__select--sort'
              value={workflowsSortParamsByString}
              onValueChange={updateSortParms}
            >
              {SORT_OPTIONS.map((option, i) => (
                <Option key={i} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </FiltersRow>
      </Card.Body>
    </Card>
  );
};

export default WorkflowsViewConfigurationBar;
